import { QueryClient } from "@tanstack/react-query";

import {
  infiniteMessagesQueryKey,
  updateInfiniteMessagesFromServer,
  updateInfiniteMessagesWithStructuredOutput,
  updateUserMessageTranscription,
} from "@/hooks/useInfiniteMessages";
import { captureException, captureMessage, logger, setTag } from "@/sentry";
import { ConversationComposition } from "@/store/conversation";

interface BaseMessage {
  type: string;
  timestamp: number; // epoch in seconds
  data?: any;
}

type ContentPositiveDeltaMessage = BaseMessage & {
  type: "content_positive_delta";
  data: string;
};

type ContentNegativeDeltaMessage = BaseMessage & {
  type: "content_negative_delta";
  data: number; // of characters removed
};

type SuggestionsMessage = BaseMessage & {
  type: "suggestions";
  data: string;
};

type MultipleChoiceMessage = BaseMessage & {
  type: "multiple_choice";
  data: string[];
};

type VoiceMessageTranscriptionMessage = BaseMessage & {
  type: "voice_message_transcription";
  data: string;
};

type CloseMessage = BaseMessage & {
  type: "close";
  data: never;
};

type PromptInfoMessage = BaseMessage & {
  type: "prompt_info";
  data: never;
};

type RxFlowConsentMessage = BaseMessage & {
  type: "rx_flow_consent";
  data: string;
};

type RxQuestionnaireStartMessage = BaseMessage & {
  type: "rx_questionnaire_start";
  data: string;
};

type RxQuestionMessage = BaseMessage & {
  type: "rx_question";
  data: string; // This is actually a JSON string that needs to be parsed
};

type RxQuestionnaireEndMessage = BaseMessage & {
  type: "rx_questionnaire_end";
  data: string;
};

type LocationQuestionMessage = BaseMessage & {
  type: "location_question";
  data: string; // JSON string, same shape as rx_question
};

type RxRenewalFlowConsentMessage = BaseMessage & {
  type: "rx_renewal_flow_consent";
  data: string;
};

type RxRenewalQuestionnaireStartMessage = BaseMessage & {
  type: "rx_renewal_questionnaire_start";
  data: string;
};

type RxRenewalQuestionMessage = BaseMessage & {
  type: "rx_renewal_question";
  data: string; // This is actually a JSON string that needs to be parsed
};

type RxRenewalQuestionnaireEndMessage = BaseMessage & {
  type: "rx_renewal_questionnaire_end";
  data: string;
};

type ClinicRequestMessage = BaseMessage & {
  type: "clinic_request";
  data: string; // JSON string of ClinicRequestContent
};

type MedicationHistoryRequestMessage = BaseMessage & {
  type: "medication_history_request";
  data: string; // JSON: { content: { consent_type }, event_id }
};

type PreAsyncRequirementsMessage = BaseMessage & {
  type: "pre_async_requirements";
  data: string; // JSON: { content: {}, event_id }
};

type UpsellMessage = BaseMessage & {
  type: "upsell";
  data: string; // JSON string of UpsellContent
};

type OutputContentMessage = BaseMessage & {
  type: "output_content";
  data: string;
};

type AskUserQuestionsMessage = BaseMessage & {
  type: "ask_user_questions";
  data: string;
};

type DocumentPreviewMessage = BaseMessage & {
  type: "document_preview";
  data: string;
};

type CommentableContentMessage = BaseMessage & {
  type: "commentable_content";
};

type ZocdocBookingMessage = BaseMessage & {
  type: "zocdoc_booking";
  data: string;
};

type ContentBoundaryMessage = BaseMessage & {
  type: "content_boundary";
  data: string;
};

export type ServerSentMessage =
  | ContentPositiveDeltaMessage
  | ContentNegativeDeltaMessage
  | PromptInfoMessage
  | SuggestionsMessage
  | CloseMessage
  | MultipleChoiceMessage
  | VoiceMessageTranscriptionMessage
  | RxFlowConsentMessage
  | RxQuestionnaireStartMessage
  | RxQuestionMessage
  | RxQuestionnaireEndMessage
  | LocationQuestionMessage
  | RxRenewalFlowConsentMessage
  | RxRenewalQuestionnaireStartMessage
  | RxRenewalQuestionMessage
  | RxRenewalQuestionnaireEndMessage
  | ClinicRequestMessage
  | MedicationHistoryRequestMessage
  | PreAsyncRequirementsMessage
  | UpsellMessage
  | OutputContentMessage
  | AskUserQuestionsMessage
  | DocumentPreviewMessage
  | CommentableContentMessage
  | ZocdocBookingMessage
  | ContentBoundaryMessage;

type ServerSentError = {
  detail?: unknown;
  status_code?: unknown;
};

export type ServerSentEvent = {
  event: string;
  data?: ServerSentMessage | ServerSentError;
};

const isExpectedClientError = (data: ServerSentEvent["data"]) => {
  if (!data || !("status_code" in data)) {
    return false;
  }
  const statusCode = data.status_code;
  return (
    typeof statusCode === "number" && statusCode >= 400 && statusCode < 500
  );
};

const DECODER = new TextDecoder("utf-8");
const EVENT_REGEX = new RegExp("^event: (.*)$");
const DATA_REGEX = new RegExp("^data: (.*)$");

export const parseIndividualEvent = (eventString: string) => {
  const [prefixedEvent, prefixedData = ""] = eventString.split("\n");
  const event = prefixedEvent.match(EVENT_REGEX)?.[1];
  const data = prefixedData.match(DATA_REGEX)?.[1];
  if (!event) return;
  const serverSentEvent: ServerSentEvent = { event };
  if (data) {
    serverSentEvent.data = JSON.parse(data) as ServerSentMessage;
  }
  return serverSentEvent;
};

export const parseEventStream = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  queryClient: QueryClient,
  setIsThinking: ConversationComposition["setIsThinking"],
  setInputControl: ConversationComposition["setInputControl"],
  conversationId: string,
) => {
  const parsedEvents: Array<ServerSentEvent> = [];
  try {
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const eventsString = DECODER.decode(value, { stream: true });
      const eventsAsStrings = eventsString.trim().split("\n\n");
      for (const eventString of eventsAsStrings) {
        let serverSentEvent: ServerSentEvent | undefined;
        try {
          serverSentEvent = parseIndividualEvent(buffer + eventString);
          buffer = "";
        } catch {
          if (!buffer) {
            // Slow connection has split an event across multiple chunks, push to buffer
            buffer += eventString;
          } else {
            // Buffer data has twice in a row failed to parse, so it's likely corrupted.
            // Assume the latest event is valid instead of cludging it onto the corrupted buffer.
            // Once the stream ends, the invalidated query will heal the message.
            const oldBuffer = buffer;
            buffer = "";
            try {
              serverSentEvent = parseIndividualEvent(eventString);
            } catch (e) {
              captureException(e, {
                data: {
                  oldBuffer,
                  eventString,
                  reason: "Failed to heal from corrupted buffer",
                },
              });
            }
          }
        }
        if (serverSentEvent) {
          parsedEvents.push(serverSentEvent);
          await ingestSSE(
            queryClient,
            setIsThinking,
            setInputControl,
            conversationId,
            serverSentEvent,
          );
        }
      }
    }
  } catch (error) {
    captureException(error, {
      data: {
        reason: "Stream parsing error",
        conversationId,
      },
    });
  } finally {
    logger.info("Stream parsing finished", {
      category: "api",
    });
    setIsThinking(false, conversationId);
    if (reader) {
      reader.releaseLock();
    }
  }
  return parsedEvents;
};

const MAX_SUGGESTION_LENGTH = 50;

/**
 * Apply an rx card (question or location) carried on the stream straight to the message cache so it
 * renders the instant the event arrives — like the other structured-output events. The prior behavior
 * only invalidated and refetched, which threw the in-hand payload away and raced the DB write
 * (read-after-write), leaving the panel blank until the 30s poll. The backend persists renewal
 * questions as rx_question, so map the stream type to the persisted type useRxSurvey reads.
 */
const applyRxCardToCache = (
  queryClient: QueryClient,
  conversationId: string,
  type: "rx_question" | "location_question" | "rx_renewal_question",
  serverSentMessageData: string,
) => {
  const parsed = JSON.parse(serverSentMessageData);
  queryClient.setQueryData(
    infiniteMessagesQueryKey(conversationId),
    updateInfiniteMessagesWithStructuredOutput(
      conversationId,
      parsed.content ?? parsed,
      type === "location_question" ? "location_question" : "rx_question",
      parsed.event_id ?? undefined,
    ),
  );
};

export const ingestSSE = async (
  queryClient: QueryClient,
  setIsThinking: ConversationComposition["setIsThinking"],
  setInputControl: ConversationComposition["setInputControl"],
  conversationId: string,
  event: ServerSentEvent,
) => {
  const queryKey = infiniteMessagesQueryKey(conversationId);
  if (event.event === "error") {
    setIsThinking(false, conversationId);
    if (!isExpectedClientError(event.data)) {
      captureMessage("Server-sent error event", {
        extra: { data: event.data },
      });
    }
  } else if (event.event === "message") {
    const { type, data: serverSentMessageData } =
      event.data as ServerSentMessage;
    if (
      // Don't log these very verbose SSE types
      ![
        "content_negative_delta",
        "content_positive_delta",
        "prompt_info",
      ].includes(type)
    ) {
      logger.info(logger.fmt`Received SSE of type ${type}`, {
        category: "api",
        serverSentEventType: type,
        serverSentEventData: serverSentMessageData,
      });
    }
    if (!["voice_message_transcription", "content_boundary"].includes(type)) {
      setIsThinking(false, conversationId);
    }
    switch (type) {
      case "content_boundary":
      case "content_negative_delta":
      case "content_positive_delta": {
        await queryClient.cancelQueries({ queryKey });

        queryClient.setQueryData(
          queryKey,
          updateInfiniteMessagesFromServer(
            conversationId,
            serverSentMessageData,
            type,
          ),
        );
        break;
      }
      case "output_content":
      case "ask_user_questions":
      case "document_preview":
      case "commentable_content":
      case "medication_history_request":
      case "pre_async_requirements": {
        await queryClient.cancelQueries({ queryKey });

        const parsed = JSON.parse(serverSentMessageData);
        const content = parsed.content ?? parsed;
        const eventId = parsed.event_id ?? undefined;

        queryClient.setQueryData(
          queryKey,
          updateInfiniteMessagesWithStructuredOutput(
            conversationId,
            content,
            type,
            eventId,
          ),
        );
        break;
      }
      case "zocdoc_booking": {
        await queryClient.cancelQueries({ queryKey });
        const parsed = JSON.parse(serverSentMessageData);
        queryClient.setQueryData(
          queryKey,
          updateInfiniteMessagesWithStructuredOutput(
            conversationId,
            parsed,
            type,
          ),
        );
        break;
      }
      case "suggestions": {
        try {
          const parsed = JSON.parse(serverSentMessageData) as {
            suggestions?: string[];
            lock_input?: boolean;
          };

          const rawSuggestions = parsed.suggestions ? parsed.suggestions : [];
          const lockInput = Boolean(parsed.lock_input);

          const suggestions = rawSuggestions
            .map((s: string) => s.trim())
            .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1))
            .filter((s: string) => Boolean(s));

          const maxLengthExceeded = suggestions.some((s: string) => {
            if (s.length > MAX_SUGGESTION_LENGTH) {
              captureMessage(
                `Suggestion exceeded max length of ${MAX_SUGGESTION_LENGTH}`,
                {
                  extra: { suggestion: s },
                },
              );
              return true;
            }
            return false;
          });
          if (suggestions.length === 1 && suggestions[0] === "None") break;
          // If any suggestion exceeds the max length, don't update the state
          if (maxLengthExceeded) break;
          setInputControl(
            {
              type: "suggestions",
              suggestions,
              lockInput,
            },
            conversationId,
          );
        } catch {
          logger.error("Failed to parse suggestions", {
            category: "api",
            serverSentMessageData,
          });
          return;
        }

        break;
      }
      case "multiple_choice": {
        const suggestions = (JSON.parse(serverSentMessageData) as string[])
          .map((s: string) => s.trim())
          .filter((s: string) => Boolean(s));
        setInputControl(
          {
            type: "multiple_choice",
            suggestions,
            selected: [],
          },
          conversationId,
        );
        break;
      }
      case "voice_message_transcription": {
        const transcription = serverSentMessageData as string;
        setIsThinking(true, conversationId);

        await queryClient.cancelQueries({ queryKey });

        queryClient.setQueryData(
          queryKey,
          updateUserMessageTranscription(conversationId, transcription),
        );
        break;
      }
      case "prompt_info": {
        break;
      }
      case "rx_question":
      case "location_question":
      case "rx_renewal_question": {
        setTag(`event.received_${type}`, true);
        await queryClient.cancelQueries({ queryKey });
        applyRxCardToCache(
          queryClient,
          conversationId,
          type,
          serverSentMessageData,
        );
        break;
      }
      case "rx_flow_consent":
      case "rx_questionnaire_start":
      case "rx_questionnaire_end":
      case "rx_renewal_flow_consent":
      case "rx_renewal_questionnaire_start":
      case "rx_renewal_questionnaire_end":
      case "clinic_request":
      case "upsell": {
        setTag(`event.received_${type}`, true);
        await queryClient.invalidateQueries({
          queryKey: ["conversation", "infinite", conversationId],
          exact: false,
        });
        break;
      }
      default: {
        captureMessage("Server-sent message event of unknown type", {
          extra: { type, serverSentMessageData },
        });
        break;
      }
    }
  } else {
    captureMessage("Server-sent unknown, valid event", {
      extra: { event: event.event, data: event.data },
    });
  }
};
